import React, { useState, useEffect, useCallback } from 'react';
import { HStack, Input, Select, FormControl, FormLabel,
  Box, Text, Badge, useToast, Flex, Table, Thead, Tbody, Tr, Th, Td, IconButton } from '@chakra-ui/react';
import { FiChevronLeft, FiChevronRight, FiCheck, FiX, FiDollarSign } from 'react-icons/fi';
import api from '../api';
import PageShell from './PageShell';
import { useAuth } from '../contexts/AuthContext';

const STATUS_COLORS = { draft: 'gray', submitted: 'blue', approved: 'green', rejected: 'red', paid: 'teal' };

export default function MileageReportsModal({ isOpen, onClose, fullPage, onBack }) {
  const active = fullPage || isOpen;
  const { user } = useAuth();
  const [data, setData] = useState([]);
  const [total, setTotal] = useState(0);
  const [page, setPage] = useState(1);
  const [perPage] = useState(50);
  const [totalPages, setTotalPages] = useState(1);
  const [users, setUsers] = useState([]);
  const [filterUser, setFilterUser] = useState('');
  const [filterStatus, setFilterStatus] = useState('');
  const [filterFrom, setFilterFrom] = useState('');
  const [filterTo, setFilterTo] = useState('');
  const toast = useToast();
  const isAccounts = user?.role === 'admin' || user?.role === 'accounts_manager';

  const fetch = useCallback(async () => {
    try {
      const params = new URLSearchParams({ page, per_page: perPage });
      if (filterUser) params.set('user_id', filterUser);
      if (filterStatus) params.set('status', filterStatus);
      if (filterFrom) params.set('date_from', filterFrom);
      if (filterTo) params.set('date_to', filterTo);
      const { data: res } = await api.get(`/mileage/claims?${params}`);
      setData(res.data || []); setTotal(res.total); setTotalPages(res.total_pages);
    } catch(e) {}
  }, [page, perPage, filterUser, filterStatus, filterFrom, filterTo]);

  useEffect(() => { if (active) { api.get('/calendar/users').then(r => setUsers(r.data)).catch(()=>{}); setPage(1); } }, [isOpen]);
  useEffect(() => { if (isOpen) fetch(); }, [isOpen, fetch]);

  const handleApprove = async (id, approved) => {
    try { await api.post(`/mileage/claims/${id}/approve`, { approved }); await fetch(); } catch(e) {}
  };
  const handleCreateExpense = async (id) => {
    try { await api.post(`/mileage/claims/${id}/create-expense`); toast({ title: 'Expense created', status: 'success', duration: 1500 }); await fetch(); } catch(e) {}
  };
  const handlePay = async (id) => {
    try { await api.post(`/mileage/claims/${id}/pay`, { payment_reference: '' }); await fetch(); } catch(e) {}
  };
  const fmtDate = (d) => d ? new Date(d.split('T')[0] + 'T12:00:00').toLocaleDateString('en-GB', { day:'numeric', month:'short', year:'2-digit' }) : '—';

  const totalMiles = data.reduce((s, c) => s + parseFloat(c.miles || 0), 0);
  const totalAmount = data.reduce((s, c) => s + parseFloat(c.total_amount || 0), 0);

  return (
    <PageShell fullPage={fullPage} onBack={onBack} isOpen={isOpen} onClose={onClose} title="Mileage Reports">
          <Box bg="gray.50" p={3} borderRadius="md" mb={3}>
            <HStack spacing={2} flexWrap="wrap">
              <FormControl w="140px"><FormLabel fontSize="xs" mb={0.5}>User</FormLabel>
                <Select size="sm" value={filterUser} onChange={e => { setFilterUser(e.target.value); setPage(1); }}>
                  <option value="">All</option>{users.map(u => <option key={u.id} value={u.id}>{u.display_name}</option>)}</Select></FormControl>
              <FormControl w="130px"><FormLabel fontSize="xs" mb={0.5}>Status</FormLabel>
                <Select size="sm" value={filterStatus} onChange={e => { setFilterStatus(e.target.value); setPage(1); }}>
                  <option value="">All</option><option value="draft">Draft</option><option value="approved">Approved</option>
                  <option value="rejected">Rejected</option><option value="paid">Paid</option></Select></FormControl>
              <FormControl w="130px"><FormLabel fontSize="xs" mb={0.5}>From</FormLabel>
                <Input size="sm" type="date" value={filterFrom} onChange={e => { setFilterFrom(e.target.value); setPage(1); }} /></FormControl>
              <FormControl w="130px"><FormLabel fontSize="xs" mb={0.5}>To</FormLabel>
                <Input size="sm" type="date" value={filterTo} onChange={e => { setFilterTo(e.target.value); setPage(1); }} /></FormControl>
            </HStack>
            <Flex mt={2} justify="space-between">
              <Text fontSize="11px" color="gray.500">{total} claims · {totalMiles.toFixed(1)} miles · £{totalAmount.toFixed(2)} total (this page)</Text>
            </Flex>
          </Box>

          <Table size="sm"><Thead><Tr>
            <Th px={2}>Date</Th><Th px={2}>Claimant</Th><Th px={2}>Project</Th><Th px={2}>Task</Th>
            <Th px={2}>From → To</Th><Th px={2}>Miles</Th><Th px={2}>Rate</Th><Th px={2}>Amount</Th>
            <Th px={2}>Reason</Th><Th px={2}>Status</Th><Th px={2}>Ref</Th>{isAccounts && <Th px={2}>Actions</Th>}
          </Tr></Thead><Tbody>
            {data.map(c => (
              <Tr key={c.id} _hover={{ bg:'gray.50' }}>
                <Td px={2} whiteSpace="nowrap">{fmtDate(c.claim_date)}</Td>
                <Td px={2}>{c.user_name || '—'}</Td>
                <Td px={2} maxW="120px" isTruncated>{c.project_name || '—'}</Td>
                <Td px={2} maxW="100px" isTruncated>{c.task_title || '—'}</Td>
                <Td px={2} maxW="150px" isTruncated>
                  {c.start_address || '?'} → {c.end_address || '?'}
                </Td>
                <Td px={2} fontWeight="600">{parseFloat(c.miles).toFixed(1)}</Td>
                <Td px={2}>£{parseFloat(c.rate_per_mile).toFixed(4)}</Td>
                <Td px={2} fontWeight="700" color="blue.600">£{parseFloat(c.total_amount).toFixed(2)}</Td>
                <Td px={2} maxW="120px" isTruncated>{c.reason || '—'}</Td>
                <Td px={2}><Badge colorScheme={STATUS_COLORS[c.status]} fontSize="9px">{c.status}</Badge>
                  {c.approved_by_name && <Text fontSize="8px" color="gray.400">by {c.approved_by_name}</Text>}</Td>
                <Td px={2} fontSize="10px">{c.payment_reference || '—'}</Td>
                {isAccounts && (
                  <Td px={2}>
                    <HStack spacing={0}>
                      {c.status === 'draft' && <>
                        <IconButton icon={<FiCheck size={14} />} size="xs" variant="ghost" colorScheme="green" onClick={() => handleApprove(c.id, true)} aria-label="Approve" />
                        <IconButton icon={<FiX size={14} />} size="xs" variant="ghost" colorScheme="red" onClick={() => handleApprove(c.id, false)} aria-label="Reject" />
                      </>}
                      {c.status === 'approved' && !c.invoice_id && (
                        <IconButton icon={<FiDollarSign size={14} />} size="xs" variant="ghost" colorScheme="orange" onClick={() => handleCreateExpense(c.id)} aria-label="Create Expense" />
                      )}
                    </HStack>
                  </Td>
                )}
              </Tr>
            ))}
          </Tbody></Table>

          {totalPages > 1 && (
            <Flex justify="center" mt={3} gap={1}>
              <IconButton icon={<FiChevronLeft />} size="xs" isDisabled={page <= 1} onClick={() => setPage(p => p - 1)} aria-label="Prev" />
              <Text fontSize="11px" px={2} alignSelf="center">Page {page} of {totalPages}</Text>
              <IconButton icon={<FiChevronRight />} size="xs" isDisabled={page >= totalPages} onClick={() => setPage(p => p + 1)} aria-label="Next" />
            </Flex>
          )}
    </PageShell>
  );
}
